import stateManager from './stateManager';
import { Product, SubscriptionStatus, AuthState, TestCell, Property, BusinessUnit } from './initializer';
import { GenericObject } from './helperTypes';
import { MetaSpecsInternal } from './event';
import uuid from './uuid';
import { EVENTS as eventsConfig } from './constants';
import snakifyObject from './camelToSnake';
import { EventData } from './eventTypes';
import { structureTestCells } from './structureTestCells';
import { AdobeData, Mcid } from './adobeUtils';
import { Platform } from './getEnvironmentContext';
import { noWindow } from './noWindow';
import logger from './logger';

export type PayloadEventType = |
    'com.easybib.page_view' |
    'com.easybib.card_engagement' |
    'com.easybib.citation_created' |
    'com.easybib.paper_upload' |
    'com.easybib.transaction' |
    'com.easybib.slot_render' |
    'com.easybib.video_events';

export type SubsObj = {
    product: Product | Property;
    status: SubscriptionStatus;
};

export type EventPayloadSansAdobe = {
    data: EventData | {};
    event_id: string;
    cloud_events_version: string;
    event_type: PayloadEventType;
    event_type_version: string;
    source: string;
    event_time: string;
    content_type: string;
    extensions: {
        client_common: {
            page_name: string;
            app_name: string;
            auth_state: AuthState;
            test_cells: TestCell[] | undefined;
            url: string;
            referral_url: string;
            client_ip_address: string | undefined;
            user_agent: string;
            os: string;
            os_ver: string;
            platform: Platform;
            business_unit: BusinessUnit;
            user_subscription_status: SubsObj[];
            app_ver?: string;
            chegg_uuid?: string;
            browser_name: string;
            browser_version: string;
            analytics_module_version: string;
        };
    };
};

export type EventPayload = EventPayloadSansAdobe & {
    extensions: {
        client_common: {
            adobe_mcid: Mcid;
            adobe_sdid?: string;
        };
    };
};

const getId = ({ id }: GenericObject): string => id || uuid.get();

const getCurrentUrl = (urlFromState?: string): string => {
    let currentUrl
    if (!noWindow()) {
        // If there is a window object, always go to it for the latest URL
        currentUrl = window.location.href;
    } else if (urlFromState) {
        // If there is no window object, we're on the server side, and the
        // initiating app should have fed in the url. Use this.
        currentUrl = urlFromState;
    } else {
        // Looks like the initiating app never gave us a url.
        // Alert and return an empty string.
        logger.error(new Error('No url passed into state'))
        currentUrl = '';
    }
    return currentUrl;
};

const getCurrentReferrer = (referrerFromState?: string): string => {
    let currentReferrer
    if (!noWindow()) {
        // If there is a window object, always go to it for the latest referrer
        currentReferrer = window.document.referrer;
    } else if (referrerFromState) {
        // If there is no window object, we're on the server side, and the
        // initiating app should have fed in the referrer. Use this.
        currentReferrer = referrerFromState;
    } else {
        // Looks like the initiating app never gave us a url.
        // Alert and return an empty string.
        currentReferrer = '';
    }
    return currentReferrer;
};

type StructureData = (eventSpecs: { type: string, meta: MetaSpecsInternal, data?: EventData }) => EventPayloadSansAdobe;

export const structureData: StructureData = ({ type, data, meta: { eventTime } }) => {
    // Get CheggDB event name:
    const {
        cheggDbEventName,
        version,
    } = eventsConfig[type];

    // Gather data from state
    const {
        pageType,
        property,
        appId,
        appVersion,
        cheggUuid,
        authState,
        product,
        businessUnit,
        clientIpAddress,
        subscriptionStatus,
        testCells,
        afVersion,
        userAgent,
        browserName,
        browserVersion,
        osName,
        osVersion,
        platform,
        url: urlFromState,
        referrer: referrerFromState,
    } = stateManager.get();

    // Convert data to CheggDb field types:
    const snakeCaseObj = data ? snakifyObject(data) : {};

    // Inherit event's ID from event data, if present.  Otherwise create one:
    const id = getId(snakeCaseObj);

    // Convert test cells to proper format.
    const structuredTestCells = structureTestCells(testCells);

    // Prefer URLs from the browser, but use those from state if no window is present:
    const url = getCurrentUrl(urlFromState);
    const referrer = getCurrentReferrer(referrerFromState);

    // Create the event:
    const event = {
        data: snakeCaseObj,
        event_id: id,
        event_time: eventTime.toISOString(),
        cloud_events_version: '0.1',
        event_type: `com.${property}.${cheggDbEventName}` as PayloadEventType,
        source: `/web/${property}/${appId}`,
        event_type_version: version,
        content_type: 'application/json',
        extensions: {
            client_common: {
                url,
                platform,
                user_agent: userAgent,
                chegg_uuid: cheggUuid,
                auth_state: authState,
                client_ip_address: clientIpAddress,
                test_cells: structuredTestCells,
                referral_url: referrer,
                app_ver: appVersion,
                page_name: pageType,
                app_name: appId,
                browser_name: browserName,
                browser_version: browserVersion,
                os: osName,
                os_ver: osVersion,
                analytics_module_version: afVersion,
                business_unit: businessUnit,
                user_subscription_status: [
                    {
                        product,
                        status: subscriptionStatus,
                    },
                ],
            },
        },
    };
    return event;
};

// The acquisition of the Adobe data is asynchronous, so we save adding it till the last moment,
// to allow caching of events
export const addAdobeData = (payload: EventPayloadSansAdobe, { mcid, sdid }: AdobeData): EventPayload => {
    const payloadWithAdobe = { ...payload } as EventPayload;
    payloadWithAdobe.extensions.client_common.adobe_mcid = mcid;
    payloadWithAdobe.extensions.client_common.adobe_sdid = sdid;
    return payload as EventPayload;
};
